import dayjs, { type Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const isSameDay = (left: Date, right: Date): boolean =>
	left.getFullYear() === right.getFullYear() &&
	left.getMonth() === right.getMonth() &&
	left.getDate() === right.getDate();

export const createUtcDate = (date?: string | Date): Dayjs => dayjs.utc(date);

export const translateUtcToLocalTime = (value: string | Date): Date => {
	const parsed = createUtcDate(value);

	return parsed.isValid() ? parsed.local().toDate() : new Date(NaN);
};

export const getUtcTimestamp = (value: string | Date): number => {
	const parsed = createUtcDate(value);

	return parsed.isValid() ? parsed.valueOf() : Number.NaN;
};

export const formatLastModified = (input: string | Date): string => {
	const date = translateUtcToLocalTime(input);
	if (Number.isNaN(date.getTime())) {
		return "recently";
	}

	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffSec = diffMs / 1000;

	if (diffSec < 60) {
		return "few seconds ago";
	}

	const diffMin = diffSec / 60;
	if (diffMin < 60) {
		const minutes = Math.floor(diffMin);

		return `${minutes} min${minutes !== 1 ? "s" : ""} ago`;
	}

	const diffHours = diffMin / 60;
	if (diffHours < 6 && isSameDay(date, now)) {
		return date.toLocaleTimeString([], {
			hour: "numeric",
			minute: "2-digit"
		});
	}

	if (diffHours < 24) {
		const hours = Math.floor(diffHours);

		return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
	}

	const diffDays = diffHours / 24;
	if (diffDays < 7) {
		const days = Math.floor(diffDays);

		return `${days} day${days !== 1 ? "s" : ""} ago`;
	}

	// Weeks run until the months branch can say at least 1: stopping at 4 weeks (28 days) left 28-29
	// days to Math.floor(28 / 30), which printed "0 month ago" (TTAPI-24).
	if (diffDays < 30) {
		const weeks = Math.floor(diffDays / 7);

		return `${weeks} week${weeks !== 1 ? "s" : ""} ago`;
	}

	// The same gap one level up: 360-364 days is 12 thirty-day months but under a year, and fell
	// through to "0 years ago". Months run until the years branch can say at least 1.
	if (diffDays < 365) {
		const months = Math.floor(diffDays / 30);

		return `${months} month${months !== 1 ? "s" : ""} ago`;
	}

	const diffYears = diffDays / 365;
	const years = Math.floor(diffYears);

	return `${years} year${years !== 1 ? "s" : ""} ago`;
};
